import { getSha256Hash } from "~/utils/encode";

const buildUserContext = () => {
  const accountStore = useAccountStore();
  const userId = accountStore.account?.customerId;
  return {
    userId,
    user: {
      userid: userId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
  };
};

const mapProduct = (orderItem: any) => ({
  name: orderItem?.product?.name,
  id: orderItem?.product?.id,
  price: getReliablePrice(orderItem?.product),
  category: orderItem?.product?.category?.[0]?.code,
  quantity: orderItem?.quantity ?? 1,
});

const mapProductFromObject = (product: any, quantity: number = 1) => ({
  name: product?.name,
  id: product?.id,
  price: getReliablePrice(product),
  category: product?.category?.[0]?.code,
  quantity,
});

export const loginEvent = async (email: string) => {
  const emailHash = await getSha256Hash(email);

  pushDataLayer({
    ...buildUserContext(),
    event: "Login",
    crto: {
      email: emailHash,
    },
  });
};

export const routeChangeEvent = (route: string, previousRoute: string) => {
  pushDataLayer({
    event: "uiRouteChange",
    attributes: {
      route,
      previousRoute,
    },
  });
};

export const checkoutStartEvent = (cart: any) => {
  const accountStore = useAccountStore();
  if (!cart) return;
  if (!accountStore.account?.customerId) return;

  const productNames = cart.orderItems.map((i) => i.product?.name).join();
  const products = cart.orderItems.map((i) => {
    i = {
      name: i.product?.name,
      id: i.product?.id,
      price: getReliablePrice(i.product),
      category: i.product?.category[0]?.code,
      quantity: i.quantity,
    };
    return i;
  });

  const isFirstSignUp = accountStore.account?.isNewCustomer
    ? "New Signup"
    : "Existing User";

  const event = {
    userId: accountStore.account?.customerId,
    isFirstSignUp,
    event: "SaleStart",
    productName: productNames,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    ecommerce: {
      detail: {
        actionField: {
          list: "OnePass mobile",
        },
        products: products,
      },
    },
  };

  pushDataLayer(event);
};

export const checkoutStageChangeEvent = (cart: any, stage: number) => {
  const accountStore = useAccountStore();
  if (!cart) return;
  if (!accountStore.account?.customerId) return;

  const productNames = cart.orderItems.map((i) => i.product?.name).join();
  const products = cart.orderItems.map((i) => {
    i = {
      name: i.product?.name,
      id: i.product?.id,
      price: getReliablePrice(i.product),
      category: i.product?.category[0]?.code,
      quantity: i.quantity,
    };
    return i;
  });
  const isFirstSignUp = accountStore.account?.isNewCustomer
    ? "New Signup"
    : "Existing User";
  const event = {
    userId: accountStore.account?.customerId,
    isFirstSignUp,
    event: "Checkout",
    productName: productNames,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    ecommerce: {
      checkout: {
        actionField: {
          step: stage,
        },
        products: products,
      },
    },
  };

  pushDataLayer(event);
};

export const orderCompleteEvent = (cart: any) => {
  const accountStore = useAccountStore();
  const simpleOrderStore = useSimpleOrderStore();
  if (!cart) return;
  if (!accountStore.account?.customerId) return;

  const shippingPrice =
    simpleOrderStore.simpleOrderPayload.shipmentMethod === "STANDARD" ? 0 : 9;

  const productNames = cart.orderItems.map((i) => i.product?.name).join();

  let coupon = null;
  const products = cart.orderItems.map((i) => {
    i = {
      name: i.product?.name,
      id: i.product?.id,
      price: getReliablePrice(i.product),
      category: i.product?.category[0]?.code,
      quantity: i.quantity,
    };
    return i;
  });
  const isFirstSignUp = accountStore.account?.isNewCustomer
    ? "New Signup"
    : "Existing User";
  const event = {
    userId: accountStore.account?.customerId,
    isFirstSignUp,
    event: "SaleComplete",
    productName: productNames,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    ecommerce: {
      purchase: {
        actionField: {
          id: cart.cart_id,
          revenue: cart.total,
          affiliation: null,
          shipping: shippingPrice,
          coupon: coupon,
        },
        products: products,
      },
    },
  };
  pushDataLayer(event);
};

export const activationStartEvent = (activation: any) => {
  activationEvent(activation, "ActivationStart");
};

export const activationCompleteEventNormal = (activation: any) => {
  activationEvent(activation, "ActivationComplete");
};
export const activationStageChangeEvent = (activation: any, stage: string) => {
  activationEvent(activation, stage || activation.stage);
};

export const rechargeEvent = (eventData: any, eventName: string) => {
  const accountStore = useAccountStore();
  const event = {
    userId: accountStore.account?.customerId,
    event: eventName,
    productName: eventData.name,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    productDetails: {
      products: [
        {
          name: eventData.name,
          id: eventData.productId,
          price: eventData.price,
          quantity: 1,
        },
      ],
    },
  };

  pushDataLayer(event);
};

export const changePlanEvent = (eventData: any, eventName: string) => {
  const accountStore = useAccountStore();
  const event = {
    userId: accountStore.account?.customerId,
    event: eventName,
    productName: eventData.name,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    productDetails: {
      currentProduct: {
        name: eventData.name,
        id: eventData.productId,
        price: eventData.price,
        quantity: 1,
      },
      targetProduct: {
        name: eventData.targetProductName,
        id: eventData.targetProductId,
        price: eventData.targetProductPrice,
        quantity: eventData.targetProductId ? 1 : 0,
      },
    },
  };

  pushDataLayer(event);
};

export const addonEvent = (eventData: any, eventName: string) => {
  //TODO: make addons clear
  const accountStore = useAccountStore();
  const event = {
    userId: accountStore.account?.customerId,
    event: eventName,
    productName: eventData.name,
    user: {
      userid: accountStore.account?.customerId,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    ecommerce: {
      products: [
        {
          name: eventData.name,
          id: eventData.productId,
          price: eventData.price,
          quantity: 1,
        },
      ],
    },
  };

  if (eventData.transactionId) {
    event.ecommerce["detail"] = {
      actionField: {
        id: eventData.transactionId,
        revenue: eventData.price,
      },
    };
  } else {
    event.ecommerce["detail"] = {
      actionField: {
        list: "Extra addons",
      },
    };
  }

  pushDataLayer(event);
};

const activationEvent = (activation: any, stage: string) => {
  const customer = activation.customer;

  if (!customer || !customer.id) {
    console.warn("Activation event skipped: No customer data available.");
    return;
  }

  const event: any = {
    userId: customer.id,
    event: stage,
    user: {
      userid: customer.id,
      profile: {
        ageRange: null,
        country: "AU",
        postcode: null,
      },
    },
    autoRecharge: activation.autoRecharge ? "ON" : "OFF",
    isPortIn: activation.type == "PORT",
  };

  try {
    const activationStore = useActivationStore();
    const yourNumberData = activationStore.yourNumberData as any;
    if (yourNumberData) {
      event.selectedMobileServiceNumber =
        yourNumberData.portedNumber ? "YES" : "NO";
      if (yourNumberData.isPrimaryContactNumber) {
        event.isPrimaryContactNumber = yourNumberData.isPrimaryContactNumber;
      }
    }
  } catch (e) {
    // activationStore not available in this context — skip enrichment
  }

  try {
    const simProductStore = useSimProductStore();
    const product = simProductStore.product as any;
    if (product) {
      event.plan = {
        name: product.name,
        dataAllowanceMb: product.allowanceMb ?? null,
        price: product.retailPrice ?? null,
        validityDays: product.validity ?? null,
        planType: product.planType ?? null,
      };
    }
  } catch (e) {
    // simProductStore not available in this context — skip enrichment
  }

  pushDataLayer(event);
};

export const viewItemEvent = (product: any, quantity: number = 1) => {
  if (!product) return;

  const item = mapProductFromObject(product, quantity);

  pushDataLayer({
    ...buildUserContext(),
    event: "view_item",
    productName: item.name,
    ecommerce: {
      detail: {
        actionField: { list: "OnePass mobile - PDP" },
        products: [item],
      },
    },
  });
};

export const addToCartEvent = (product: any, quantity: number = 1) => {
  if (!product) return;

  const item = mapProductFromObject(product, quantity);

  pushDataLayer({
    ...buildUserContext(),
    event: "add_to_cart",
    productName: item.name,
    ecommerce: {
      add: {
        actionField: { list: "OnePass mobile - PDP" },
        products: [item],
      },
    },
  });
};

export const viewCartEvent = (cart: any) => {
  if (!cart?.orderItems?.length) return;

  const products = cart.orderItems.map(mapProduct);
  const productNames = products.map((p: { name: any }) => p.name).join();

  pushDataLayer({
    ...buildUserContext(),
    event: "view_cart",
    productName: productNames,
    ecommerce: {
      detail: {
        actionField: { list: "OnePass mobile - Cart" },
        products,
      },
    },
  });
};

export const verify2FAEvent = (cart: any) => {
  const products = cart?.orderItems?.length
    ? cart.orderItems.map(mapProduct)
    : [];

  pushDataLayer({
    ...buildUserContext(),
    event: "verify_2fa",
    ecommerce: {
      checkout: {
        actionField: { step: "2fa" },
        products,
      },
    },
  });
};

export const loginErrorEvent = (errorReason: string, errorMessage: string) => {
  pushDataLayer({
    event: "error",
    error_reason: errorReason || "unknown",
    error_message: errorMessage || "",
    error_source: "login",
  });
};

export const signupErrorEvent = (errorReason: string, errorMessage: string) => {
  pushDataLayer({
    event: "error",
    error_reason: errorReason || "unknown",
    error_message: errorMessage || "",
    error_source: "signup",
  });
};

export const signupPageViewEvent = () => {
  if (typeof window === "undefined") return;

  pushDataLayer({
    event: "page_view",
    page_domain: window.location.hostname,
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: typeof document !== "undefined" ? document.title : "",
    page_url: window.location.href,
  });
};

const pushDataLayer = (data: any) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(data);
};
